/**
 * Audio Manager Hook
 * Centralized audio playback and cleanup management
 * 
 * Phase 3 Refactoring - Extracted from useGameActions.js
 */

import { useRef, useCallback } from 'react';

/**
 * Custom hook to manage audio playback with automatic cleanup
 * 
 * Features:
 * - Tracks all active audio instances
 * - Auto-cleanup when new audio plays
 * - Provides utilities to stop all audio
 * - Handles audio play errors gracefully
 * 
 * @returns {Object} Audio management utilities
 */
export function useAudioManager() {
  // Track all active Audio instances for cleanup
  const activeAudioRef = useRef([]);
  
  /**
   * Play audio with automatic cleanup of previous audio
   * @param {string} audioSrc - Path to audio file
   * @returns {Audio} The Audio instance that was created
   */
  const playAudio = useCallback((audioSrc) => {
    // Stop all previous audio
    activeAudioRef.current.forEach(audio => {
      audio.pause();
      audio.currentTime = 0;
    });
    activeAudioRef.current = [];
    
    // Create and track new audio
    const audio = new Audio(audioSrc);
    activeAudioRef.current.push(audio);
    
    // Remove from tracking when ended
    audio.addEventListener('ended', () => {
      activeAudioRef.current = activeAudioRef.current.filter(a => a !== audio);
    });
    
    // Play with error handling
    audio.play().catch(err => console.log('Audio play failed:', err));
    
    return audio;
  }, []);
  
  /**
   * Play audio without stopping previous audio
   * Useful for overlapping sound effects
   * @param {string} audioSrc - Path to audio file
   * @param {Object} options - Audio options { volume, startTime, loop }
   * @returns {Audio} The Audio instance that was created
   */
  const playAudioOverlap = useCallback((audioSrc, options = {}) => {
    const audio = new Audio(audioSrc);
    
    // Apply options
    if (options.volume !== undefined) {
      audio.volume = options.volume;
    }
    if (options.startTime !== undefined) {
      audio.currentTime = options.startTime;
    }
    if (options.loop !== undefined) {
      audio.loop = options.loop;
    }
    
    // Track audio instance
    activeAudioRef.current.push(audio);
    
    // Remove from tracking when ended (if not looping)
    if (!options.loop) {
      audio.addEventListener('ended', () => {
        activeAudioRef.current = activeAudioRef.current.filter(a => a !== audio);
      });
    }
    
    // Play with error handling
    audio.play().catch(err => console.log('Audio overlap play failed:', err));
    
    return audio;
  }, []);
  
  /**
   * Stop all tracked audio instances
   */
  const stopAllTrackedAudio = useCallback(() => {
    activeAudioRef.current.forEach(audio => {
      audio.pause();
      audio.currentTime = 0;
    });
    activeAudioRef.current = [];
  }, []);
  
  /**
   * Stop all audio elements in the DOM (including untracked ones)
   */
  const stopAllDomAudio = useCallback(() => {
    document.querySelectorAll('audio').forEach(audio => {
      audio.pause();
      audio.currentTime = 0;
    });
  }, []);
  
  /**
   * Stop all audio (both tracked and DOM)
   */
  const stopAllAudio = useCallback(() => {
    // Stop tracked audio
    activeAudioRef.current.forEach(audio => {
      audio.pause();
      audio.currentTime = 0;
    });
    activeAudioRef.current = [];
    
    // Also stop any DOM audio elements (backup)
    document.querySelectorAll('audio').forEach(audio => {
      audio.pause();
      audio.currentTime = 0;
    });
  }, []);
  
  /**
   * Get count of currently playing audio instances
   * @returns {number} Number of active audio instances
   */
  const getActiveAudioCount = useCallback(() => {
    return activeAudioRef.current.length;
  }, []);
  
  /**
   * Fade out all audio over a duration
   * @param {number} duration - Fade duration in milliseconds
   */
  const fadeOutAllAudio = useCallback((duration = 1000) => {
    const fadeStep = 50; // Fade every 50ms
    const steps = duration / fadeStep;
    
    activeAudioRef.current.forEach(audio => {
      const initialVolume = audio.volume;
      const volumeStep = initialVolume / steps;
      let currentStep = 0;
      
      const fadeInterval = setInterval(() => {
        currentStep++;
        const newVolume = Math.max(0, initialVolume - (volumeStep * currentStep));
        audio.volume = newVolume;
        
        if (currentStep >= steps || newVolume <= 0) {
          clearInterval(fadeInterval);
          audio.pause();
          audio.currentTime = 0;
        }
      }, fadeStep);
    });
    
    // Clear tracked audio after fade completes
    setTimeout(() => {
      activeAudioRef.current = [];
    }, duration);
  }, []);
  
  return {
    // Main playback functions
    playAudio,
    playAudioOverlap,
    
    // Stop functions
    stopAllTrackedAudio,
    stopAllDomAudio,
    stopAllAudio,
    
    // Utilities
    getActiveAudioCount,
    fadeOutAllAudio,
    
    // Direct ref access (for advanced use cases)
    activeAudioRef
  };
}
